import {
	BarcodeOutlined,
	SearchOutlined,
	SunOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import React, { ChangeEvent, useCallback, useMemo, useState } from "react";
import { getEmployeeList } from "src/apis/employeeApi";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { KioskPillSwitchButtonForm } from "src/components/common/form/KioskPillSwitchButtonForm";
import { KioskCountPanelForm } from "src/components/common/form/KioskCountPanelForm";
import { KioskWorkStandardButtonForm } from "src/components/common/form/KioskWorkStandardButtonForm";
import { KioskWorkTimeButtonForm } from "src/components/common/form/KioskWorkTimeButtonForm";
import { KIOSK_FORM_TITLE_STYLE, KioskFormTitle } from "src/components/common/form/KioskFormTitle";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { useDescText } from "src/hooks/useDescText";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { InfoCodeSearchFieldConfig, InfoFieldHandlers } from "src/types/info-form";
import { DESC_BTN, DESC_FLD } from "src/utils/descLanguage/descLanguageUtil";
import { formatDate } from "src/utils/string/stringUtil";
import {
	KSK2022_R01HeaderDto,
	KSK2022_R01LotDto,
	KSK2022_R01WorkTimeProcessType,
} from "../Dto_KSK2022_R01";
import { calculateLotQty, useKSK2022_R01Store } from "../Store_KSK2022_R01";

interface Props {
	defaultKSK2022_R01HeaderRow: KSK2022_R01HeaderDto;
	defaultKSK2022_R01LotRow: KSK2022_R01LotDto;
	getProductWorkFieldHandlers: (fieldName: string) => InfoFieldHandlers;
	handleFetchLotList: (onFail?: () => void) => void;
	handleFetchProductResult: () => Promise<void>;
	handleLotCountChange: (name: string, newValue: number) => Promise<void>;
	handleLotCountResetClick: () => Promise<void>;
	isLotCountResetting: boolean;
	handleLotCreateClick: () => Promise<void>;
	handleWorkResetClick: () => void;
	handleWorkStandardClick: (sheetNo: 1 | 2) => void;
	handleWorkTimeClick: (
		productOrderNo: string,
		processType: KSK2022_R01WorkTimeProcessType,
		manualTime?: Date,
		rawTime?: { month: string; day: string; hour: string; minute: string },
	) => Promise<boolean>;
}

export const ResultInfo_KSK2022_R01 = (props: Props) => {
	const {
		defaultKSK2022_R01HeaderRow,
		getProductWorkFieldHandlers,
		handleFetchProductResult,
		handleLotCountChange,
		handleLotCountResetClick,
		isLotCountResetting,
		handleLotCreateClick,
		handleWorkResetClick,
		handleWorkStandardClick,
		handleWorkTimeClick,
	} = props;

	const { jobTeamList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { renderDescText, getFormTitleWidth } = useDescText();
	const {
		editKSK2022_R01,
		setEditKSK2022_R01,
		KSK2022_R01Filter,
		setKSK2022_R01Filter,
		KSK2022_R01LotList,
		deletedKSK2022_R01LotList,
		modalVisible,
		setModalVisible,
	} = useKSK2022_R01Store();

	const totalLotQty = useMemo(
		() => calculateLotQty(KSK2022_R01LotList, deletedKSK2022_R01LotList),
		[KSK2022_R01LotList, deletedKSK2022_R01LotList],
	);
	const plcCountValue = editKSK2022_R01?.plcCount ?? 0;
	const lotQtyInputValue = editKSK2022_R01?.pendingLotQty ?? 0;
	const labelPrintAutoValue = KSK2022_R01Filter.labelPrintAuto === "Y" ? "Y" : "N";
	const hasWorkOrder = !!(editKSK2022_R01?.productOrderNo && editKSK2022_R01?.itemCd && editKSK2022_R01?.workplaceCd);
	const workEmpInfoHandleFoucsId = "resultInfoWorkEmpInput";
	const [productDtPickerVisible, setProductDtPickerVisible] = useState(false);
	const [productDtPickerPos, setProductDtPickerPos] = useState({
		top: 0,
		left: 0,
		height: 0,
	});
	// 현재 언어에서 가장 긴 항목명을 기준으로 본 화면의 제목 너비를 맞춘다.
	const formTitleWidth = useMemo(() => getFormTitleWidth([
		renderDescText(DESC_FLD.PRODUCTION_RESULT_NO, "생산실적번호"),
		renderDescText(DESC_FLD.PRODUCTION_DATE, "생산일자"),
		renderDescText(DESC_FLD.WORK_TEAM, "작업팀"),
		renderDescText(DESC_FLD.AUTO_LABEL_PRINT, "라벨자동출력"),
		renderDescText(DESC_FLD.OPERATOR, "작업자"),
		renderDescText(DESC_FLD.PRODUCTION_QTY, "생산량"),
		renderDescText(DESC_FLD.PRODUCTION_DEFECT_QTY, "생산불량수량"),
		renderDescText(DESC_FLD.TOTAL_PRODUCTION_QTY, "총생산량"),
	], 120, 180), [getFormTitleWidth, renderDescText]);

	const fieldWrapperOption = {
		wrapperStyle: "!w-full min-w-0 h-[38px]",
		titleStyle: KIOSK_FORM_TITLE_STYLE,
		inputStyle: "flex-1 min-w-0 [&>input]:!h-[38px] [&>input]:!rounded-[8px] [&>input]:!border-[#cbd9eb] [&>input]:px-[10px]",
	};
	const dropdownWrapperOption = {
		wrapperStyle: "!w-full min-w-0 h-[38px]",
		titleStyle: fieldWrapperOption.titleStyle,
		inputStyle: "flex-1 min-w-0 [&>select]:!h-[38px] [&>select]:!rounded-[8px] [&>select]:!border-[#cbd9eb] [&>select]:px-[8px]",
	};

	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: "empNo",
			category: "담당자",
			focusId: workEmpInfoHandleFoucsId,
			responseMapping: {
				empNo: "productEmp",
				empNm: "productEmpNm",
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList("all");
				return employeeResponse.data.data.find((value) => value.empNo === code);
			},
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: editKSK2022_R01,
		setEditData: setEditKSK2022_R01,
		defaultData: defaultKSK2022_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: "KSK2022_R01_ResultInfo",
	});

	const handleChange = useCallback(
		(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
			const { name, value } = e.target;
			setEditKSK2022_R01({
				...defaultKSK2022_R01HeaderRow,
				...editKSK2022_R01,
				[name]: value,
			});
		},
		[editKSK2022_R01, defaultKSK2022_R01HeaderRow, setEditKSK2022_R01],
	);

	const handleProductDateChange = useCallback(
		(value: Date | undefined) => {
			setEditKSK2022_R01({
				...defaultKSK2022_R01HeaderRow,
				...editKSK2022_R01,
				productDt: formatDate(value),
			});
		},
		[editKSK2022_R01, defaultKSK2022_R01HeaderRow, setEditKSK2022_R01],
	);

	const handleLotQtyChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const lotQty = Number(e.target.value) || 0;
			setEditKSK2022_R01({
				...defaultKSK2022_R01HeaderRow,
				...editKSK2022_R01,
				pendingLotQty: lotQty,
			});
		},
		[editKSK2022_R01, defaultKSK2022_R01HeaderRow, setEditKSK2022_R01],
	);

	const handleProductResultSearchClick = useCallback(() => {
		if (!editKSK2022_R01?.productResultNo) {
			getProductWorkFieldHandlers("productResultNo").onDoubleClick({
				currentTarget: { blur: () => {}, value: "" },
			} as unknown as React.MouseEvent<HTMLInputElement>);
			return;
		}
		void handleFetchProductResult();
	}, [editKSK2022_R01?.productResultNo, getProductWorkFieldHandlers, handleFetchProductResult]);

	return (
		<div className="h-full w-full min-w-0 overflow-hidden rounded-[12px] border border-[#d3e0f0] bg-white shadow-sm">
			<div className="flex h-[36px] shrink-0 items-center gap-[10px] bg-gradient-to-r from-[#123f78] to-[#245d98] px-[16px] text-[17px] font-bold text-white">
				<SunOutlined />
				<span>{renderDescText(DESC_FLD.PRODUCTION_RESULT_REGISTRATION, "생산실적 등록")}</span>
			</div>

			<div className="hide-scrollbar h-[calc(100%_-_36px)] overflow-y-auto p-[14px]">
				<KioskWorkTimeButtonForm
					workStartDt={editKSK2022_R01?.workStartDt}
					workEndDt={editKSK2022_R01?.workEndDt}
					onWorkStartClick={() => handleWorkTimeClick(editKSK2022_R01?.productOrderNo || "", "workStart")}
					onWorkEndClick={() => handleWorkTimeClick(editKSK2022_R01?.productOrderNo || "", "workEnd")}
					workStartTitle={renderDescText(DESC_FLD.WORK_START, "작업시작")}
					workEndTitle={renderDescText(DESC_FLD.WORK_END, "작업종료")}
					onResetClick={handleWorkResetClick}
					resetTitle={renderDescText(DESC_BTN.RESET, "초기화")}
				/>

				<div className="mb-[8px] flex flex-wrap items-center gap-[6px]">
					<label className="flex h-[38px] min-w-[240px] flex-1 items-center">
						<KioskFormTitle
							title={renderDescText(DESC_FLD.PRODUCTION_RESULT_NO, "생산실적번호")}
							titleWidth={formTitleWidth}
						/>
						<input
							className="h-[38px] min-w-0 flex-1 rounded-[8px] border border-[#cbd9eb] bg-white px-[10px] text-[18px]"
							name="productResultNo"
							value={editKSK2022_R01?.productResultNo || ""}
							onChange={handleChange}
							onDoubleClick={getProductWorkFieldHandlers("productResultNo").onDoubleClick}
							onKeyDown={getProductWorkFieldHandlers("productResultNo").onEnterKeyDown}
						/>
					</label>
					<button
						type="button"
						className="flex h-[38px] w-[44px] shrink-0 items-center justify-center rounded-[8px] border border-[#cbd9eb] bg-[#f7f9fc] text-[#365985] hover:bg-[#e8eff8]"
						onClick={handleProductResultSearchClick}
						title={renderDescText(DESC_BTN.FETCH, "조회")}
					>
						<SearchOutlined />
					</button>
					<KioskWorkStandardButtonForm
						isDisabled={!hasWorkOrder}
						onClick={sheetNo => void handleWorkStandardClick(sheetNo)}
					/>
				</div>

				<div className="mb-[8px] grid grid-cols-[minmax(0,1fr)_minmax(178px,0.7fr)] gap-[8px]">
					<DropDownForm
						title={renderDescText(DESC_FLD.WORK_TEAM, "작업팀")}
						titleWidth={formTitleWidth}
						name="jobTeam"
						initLabelLess={true}
						value={editKSK2022_R01?.jobTeam}
						data={jobTeamList}
						editable={true}
						onChange={handleChange}
						wrapperOption={dropdownWrapperOption}
					/>
					<KioskPillSwitchButtonForm
						title={renderDescText(DESC_FLD.AUTO_LABEL_PRINT, "라벨자동출력")}
						name="labelPrintAuto"
						value={labelPrintAutoValue}
						onChange={(name, value) => setKSK2022_R01Filter({ ...KSK2022_R01Filter, [name]: value })}
						wrapperOption={{
							wrapperStyle: "!w-full min-w-0 !h-[38px] justify-end",
							titleStyle: `${fieldWrapperOption.titleStyle} !w-auto !min-w-0 !px-[6px]`,
							inputStyle: "",
						}}
					/>
				</div>

				<div className="mb-[10px] grid grid-cols-[minmax(0,1fr)_max-content] items-center gap-[8px]">
					<label className="flex h-[38px] min-w-0 items-center">
						<KioskFormTitle
							title={renderDescText(DESC_FLD.OPERATOR, "작업자")}
							titleWidth={formTitleWidth}
						/>
						<input
							id={workEmpInfoHandleFoucsId}
							className="mr-[5px] h-[38px] w-[118px] min-w-[90px] rounded-[8px] border border-[#cbd9eb] bg-white px-[10px] text-[18px]"
							name="productEmp"
							value={editKSK2022_R01?.productEmp || ""}
							onChange={handleChange}
							onDoubleClick={getFieldHandlers("empNo").onDoubleClick}
							onKeyDown={getFieldHandlers("empNo").onEnterKeyDown}
						/>
						<input
							className="h-[38px] min-w-0 flex-1 rounded-[8px] border border-[#cbd9eb] bg-[#eef2f7] px-[10px] text-[18px]"
							value={editKSK2022_R01?.productEmpNm || ""}
							disabled
							readOnly
						/>
					</label>
					<SingleDateForm
						title={renderDescText(DESC_FLD.PRODUCTION_DATE, "생산일자")}
						titleWidth={formTitleWidth}
						value={editKSK2022_R01?.productDt ? new Date(editKSK2022_R01.productDt) : undefined}
						setValue={handleProductDateChange}
						editable={false}
						visible={productDtPickerVisible}
						setVisible={setProductDtPickerVisible}
						pickerPos={productDtPickerPos}
						setPickerPos={setProductDtPickerPos}
						wrapperOption={{
							wrapperStyle: "!m-0 h-[38px] shrink-0",
							titleStyle: fieldWrapperOption.titleStyle,
							inputStyle: "h-[38px] w-[140px] min-w-[140px] rounded-[8px] border border-[#cbd9eb] bg-[#eef2f7] px-[10px] text-[18px] cursor-default",
						}}
					/>
				</div>

				<KioskCountPanelForm
					title={renderDescText(DESC_FLD.RESULT_COUNT, "설비 Count")}
					name="plcCount"
					value={plcCountValue}
					onChange={handleLotCountChange}
					onReset={handleLotCountResetClick}
					resetTitle={renderDescText(DESC_BTN.RESET, "초기화")}
					isDisabled={isLotCountResetting}
				/>

				<div className="mb-[8px] grid grid-cols-[minmax(0,1fr)_minmax(130px,0.8fr)] gap-[6px]">
					<InputForm
						title={renderDescText(DESC_FLD.PRODUCTION_QTY, "생산량")}
						titleWidth={formTitleWidth}
						value={lotQtyInputValue}
						editable={true}
						name="pendingLotQty"
						onChange={handleLotQtyChange}
						dragSelectAll={lotQtyInputValue === 0}
						wrapperOption={fieldWrapperOption}
					/>
					<button
						type="button"
						className="flex h-[38px] items-center justify-center gap-[7px] rounded-[8px] bg-gradient-to-b from-[#2f67ec] to-[#1646b8] px-[10px] text-[16px] font-bold text-white shadow-sm hover:brightness-105"
						onClick={() => void handleLotCreateClick()}
					>
						<BarcodeOutlined />
						{renderDescText(DESC_FLD.LOT_ISSUE, "LOT 발행")}
					</button>
				</div>

				<div className="mb-[8px] grid grid-cols-[minmax(0,1fr)_minmax(130px,0.8fr)] gap-[6px]">
					<InputForm
						title={renderDescText(DESC_FLD.PRODUCTION_DEFECT_QTY, "생산불량수량")}
						titleWidth={formTitleWidth}
						value={editKSK2022_R01?.badQty || 0}
						editable={false}
						name="badQty"
						onChange={handleChange}
						wrapperOption={fieldWrapperOption}
					/>
					<button
						type="button"
						className="flex h-[38px] items-center justify-center gap-[7px] rounded-[8px] bg-gradient-to-b from-[#2f67ec] to-[#1646b8] px-[10px] text-[16px] font-bold text-white shadow-sm hover:brightness-105"
						onClick={() => setModalVisible(!modalVisible)}
					>
						<WarningOutlined />
						{renderDescText(DESC_FLD.DEFECT_DETAILS, "불량내역")}
					</button>
				</div>

				<InputForm
					title={renderDescText(DESC_FLD.TOTAL_PRODUCTION_QTY, "총생산량")}
					titleWidth={formTitleWidth}
					value={totalLotQty}
					editable={false}
					name="totalLotQty"
					onChange={handleChange}
					wrapperOption={fieldWrapperOption}
				/>
			</div>
		</div>
	);
};
